import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { Button, Modal } from 'react-bootstrap';
import { fetchClients } from '../../../store/actions/waste';
import { fetchEntries, getEntryById, addEntry, editEntry, deleteEntry } from '../../../store/actions/operationalLogEntry';
import { resetOperationalLogEntries } from '../../../store/reducers/operationalLogEntry';
import { getOperationalLogById } from '../../../store/actions/operationalLog';
import { Loader } from '../../../utils/tools';
import { formValues, buildValidation } from './validationSchema';
import OperationalLogEntryFormFields from './formFields';
import { SHUTDOWN_STATUSES } from '../../../utils/constants/shutdownStatuses';
import { LIMITS_COMPLIANCE } from '../../../utils/constants/limitsCompliance';
import { PLAN_LIMITS } from '../../../config/plans';
import './dayEditor.css';

const toWasteCodeId = (wasteCode) => (
    wasteCode && typeof wasteCode === 'object' ? wasteCode._id : (wasteCode || null)
);

const toDateKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MONTH_LABELS = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December'];

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const OperationalLogEntries = ({ operationalLogId, facilityType }) => {
    const dispatch = useDispatch();

    const tenant = useSelector(state => state.tenant.current);
    const entries = useSelector(state => state.operationalLogEntry.data);
    const loading = useSelector(state => state.operationalLogEntry.loading);
    const entryToEdit = useSelector(state => state.operationalLogEntry.entryToEdit);
    const clients = useSelector(state => state.waste.clients);
    const logResponsiblePersons = useSelector(state => state.operationalLog.logToEdit?.responsiblePersons ?? []);
    const logDefaultResponsiblePersons = useSelector(state => state.operationalLog.logToEdit?.defaultResponsiblePersons ?? []);
    const logDefaultOperatingHoursFrom = useSelector(state => state.operationalLog.logToEdit?.defaultOperatingHoursFrom ?? '');
    const logDefaultOperatingHoursTo = useSelector(state => state.operationalLog.logToEdit?.defaultOperatingHoursTo ?? '');
    const defaultOperatingHours = logDefaultOperatingHoursFrom && logDefaultOperatingHoursTo
        ? `${logDefaultOperatingHoursFrom} - ${logDefaultOperatingHoursTo}`
        : (logDefaultOperatingHoursFrom || logDefaultOperatingHoursTo || '');

    const today = useMemo(() => new Date(), []);
    const [viewMonth, setViewMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
    const [selectedDate, setSelectedDate] = useState(today);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const isCurrentMonth =
        viewMonth.getFullYear() === today.getFullYear() &&
        viewMonth.getMonth() === today.getMonth();

    // viewMonth v závislostiach = nová funkcia pri každej zmene mesiaca
    // → React vždy zavolá ref s elementom aj pri reuse DOM nodu
    const todayTabRef = useCallback((node) => {
        if (!node) return;
        node.scrollIntoView({ inline: 'center', block: 'nearest' });
    }, [viewMonth]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        dispatch(fetchClients());
    }, [dispatch]);

    useEffect(() => {
        if (operationalLogId) {
            // vymazať záznamy predtým zobrazeného denníka/mesiaca, nech sa medzičasom (kým beží fetch)
            // omylom nenavrhne zhoda s dnešným dátumom z iného denníka
            dispatch(resetOperationalLogEntries());
            dispatch(fetchEntries({ operationalLogId, year: viewMonth.getFullYear(), month: viewMonth.getMonth() + 1 }));
        }
    }, [dispatch, operationalLogId, viewMonth]);

    const refetchViewMonth = () => {
        dispatch(fetchEntries({ operationalLogId, year: viewMonth.getFullYear(), month: viewMonth.getMonth() + 1 }));
    };

    const entriesByDate = useMemo(() => {
        const map = new Map();
        (Array.isArray(entries) ? entries : []).forEach((entry) => {
            map.set(toDateKey(entry.date), entry);
        });
        return map;
    }, [entries]);

    const currentEntrySummary = entriesByDate.get(toDateKey(selectedDate));

    const formik = useFormik({
        initialValues: formValues,
        validationSchema: buildValidation(facilityType),
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: (values, { setSubmitting }) => {
            const action = currentEntrySummary
                ? editEntry({ operationalLogId, entryId: currentEntrySummary._id, values, facilityType })
                : addEntry({ operationalLogId, values, facilityType });
            dispatch(action)
                .unwrap()
                .then(() => {
                    refetchViewMonth();
                    dispatch(getOperationalLogById(operationalLogId));
                })
                .catch(() => {})
                .finally(() => setSubmitting(false));
        },
    });

    const [readyDateKey, setReadyDateKey] = useState(null);
    const isFormReady = readyDateKey === toDateKey(selectedDate);

    useEffect(() => {
        if (currentEntrySummary) {
            dispatch(getEntryById({ operationalLogId, entryId: currentEntrySummary._id }));
        } else {
            formik.resetForm({
                values: {
                    ...formValues,
                    date: selectedDate,
                    operatingHours: defaultOperatingHours,
                    responsiblePersons: [...logDefaultResponsiblePersons]
                }
            });
            setReadyDateKey(toDateKey(selectedDate));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationalLogId, selectedDate, currentEntrySummary?._id, defaultOperatingHours, logDefaultResponsiblePersons.join(' ')]);

    useEffect(() => {
        if (entryToEdit && currentEntrySummary && entryToEdit._id === currentEntrySummary._id) {
            formik.setValues({
                date: entryToEdit.date ? new Date(entryToEdit.date) : selectedDate,
                responsiblePersons: entryToEdit.responsiblePersons?.filter(Boolean) ?? [],
                wasteReceived: (entryToEdit.wasteReceived ?? []).map(w => ({ ...w, wasteCode: toWasteCodeId(w.wasteCode) })),
                wasteProcessed: (entryToEdit.wasteProcessed ?? []).map(w => ({ ...w, wasteCode: toWasteCodeId(w.wasteCode) })),
                wasteRejected: (entryToEdit.wasteRejected ?? []).map(w => ({ ...w, wasteCode: toWasteCodeId(w.wasteCode) })),
                solidLiquidWasteHandling: entryToEdit.solidLiquidWasteHandling || '',
                samples: entryToEdit.samples ?? [],
                technicalCondition: entryToEdit.technicalCondition || '',
                malfunctions: entryToEdit.malfunctions ?? [],
                operatingHours: entryToEdit.operatingHours || '',
                shutdown: entryToEdit.shutdown ?? { status: SHUTDOWN_STATUSES.NONE, reason: '' },
                maintenance: entryToEdit.maintenance ?? [],
                authorityInspections: entryToEdit.authorityInspections ?? [],
                limitsCompliance: entryToEdit.limitsCompliance || LIMITS_COMPLIANCE.COMPLIANT,
                otherActivities: entryToEdit.otherActivities || '',
                landfillDetails: {
                    hazardousWastePlacement: entryToEdit.landfillDetails?.hazardousWastePlacement || '',
                    compaction: entryToEdit.landfillDetails?.compaction || '',
                    layerThicknessAndCovering: entryToEdit.landfillDetails?.layerThicknessAndCovering || '',
                    monitoringWellSamples: entryToEdit.landfillDetails?.monitoringWellSamples ?? [],
                    inertMaterialsUsed: entryToEdit.landfillDetails?.inertMaterialsUsed ?? [],
                    waterPumping: entryToEdit.landfillDetails?.waterPumping || '',
                },
                mercuryReceived: entryToEdit.mercuryReceived ?? [],
                mercuryDispatched: entryToEdit.mercuryDispatched ?? [],
            });
            setReadyDateKey(toDateKey(selectedDate));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryToEdit]);

    const handlePrevMonth = () => {
        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
    };

    const handleGoToToday = () => {
        setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1));
        setSelectedDate(today);
    };

    const handleSelectDay = (day) => {
        setSelectedDate(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
    };

    const handleDeleteEntry = () => {
        dispatch(deleteEntry({ operationalLogId, entryId: currentEntrySummary._id })).then(() => {
            refetchViewMonth();
        });
        setShowDeleteConfirm(false);
    };

    const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const dayTabs = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const safeClients = Array.isArray(clients) ? clients : [];

    const isDemo = tenant?.plan === 'demo';
    const planLimits = PLAN_LIMITS[tenant?.plan];

    if (loading && entries.length === 0) return <Loader />;

    return (
        <div className="ol-daybook">
            {isDemo ? (
                <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <span>
                        Demo plán: v tomto denníku môžete zaznamenať maximálne <strong>{planLimits?.maxOperationalLogEntries}</strong> dní.
                    </span>
                    <Link to="/billing" className="btn btn-sm btn-warning fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                        Prejsť na Business plán
                    </Link>
                </div>
            ) : null}
            <div className="ol-ledger-wrap">
                <div className="ol-ledger-head">
                    <div className="ol-ledger-label">Vyberte deň</div>
                    <div className="ol-month-nav">
                        {!isCurrentMonth && (
                            <button type="button" className="ol-today-btn" onClick={handleGoToToday}>Dnes</button>
                        )}
                        <button type="button" onClick={handlePrevMonth}>‹</button>
                        <span className="ol-month-label">
                            {MONTH_LABELS[viewMonth.getMonth()]}
                            <select
                                className="ol-year-select"
                                value={viewMonth.getFullYear()}
                                onChange={(e) => setViewMonth(new Date(Number(e.target.value), viewMonth.getMonth(), 1))}
                            >
                                {Array.from({ length: today.getFullYear() - 2019 }, (_, i) => today.getFullYear() - i).map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </span>
                        <button type="button" onClick={handleNextMonth}>›</button>
                    </div>
                </div>
                <div className="ol-day-strip">
                    {dayTabs.map((day) => {
                        const date = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day);
                        const hasEntry = entriesByDate.has(toDateKey(date));
                        const isSelected = toDateKey(date) === toDateKey(selectedDate);
                        const isFuture = date > today;
                        const isToday = toDateKey(date) === toDateKey(today);
                        return (
                            <div
                                key={`${viewMonth.getFullYear()}-${viewMonth.getMonth()}-${day}`}
                                ref={isToday ? todayTabRef : null}
                                className={`ol-tab ${hasEntry ? 'has-entry' : isFuture ? 'future' : 'empty'}${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                                onClick={() => handleSelectDay(day)}
                            >
                                <div className="d">{String(day).padStart(2, '0')}</div>
                            </div>
                        );
                    })}
                </div>
                <div className="ol-legend">
                    <span><i style={{ background: 'var(--ol-moss)' }}></i>Má záznam</span>
                    <span><i style={{ background: '#CBD5E0' }}></i>Bez záznamu</span>
                    <span><i style={{ background: '#E2E8F0' }}></i>Budúci deň</span>
                    <span><i style={{ background: '#0B1157' }}></i>Dnes</span>
                </div>
            </div>

            <div className="ol-day-header">
                <div className="ol-date-big">{capitalize(selectedDate.toLocaleDateString('sk-SK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }))}</div>
                <div className="d-flex align-items-center gap-2">
                    <span className={`ol-pill ${currentEntrySummary ? 'has-entry' : 'empty'}`}>
                        {currentEntrySummary ? 'Záznam vytvorený' : 'Bez záznamu'}
                    </span>
                    {currentEntrySummary && (
                        <Button variant="outline-danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                            Vymazať tento záznam
                        </Button>
                    )}
                </div>
            </div>

            {!isFormReady ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
                    <Loader />
                </div>
            ) : (
                <form onSubmit={formik.handleSubmit}>
                    <OperationalLogEntryFormFields
                        key={readyDateKey}
                        formik={formik}
                        clients={safeClients}
                        facilityType={facilityType}
                        responsiblePersons={logResponsiblePersons}
                    />

                    {formik.submitCount > 0 && !formik.isValid && (
                        <div className="alert alert-danger mt-3 mb-0">
                            Formulár obsahuje chyby — skontrolujte zvýraznené polia vyššie.
                        </div>
                    )}

                    <div className="d-flex justify-content-end mt-3 mb-5">
                        <Button
                            className="button-custom-large"
                            variant="primary"
                            type="submit"
                            size="lg"
                            disabled={formik.isSubmitting}
                        >
                            {formik.isSubmitting ? <Loader /> : 'Uložiť záznam'}
                        </Button>
                    </div>
                </form>
            )}

            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Potvrdenie</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Naozaj chcete vymazať denný záznam pre tento deň?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        Zrušiť
                    </Button>
                    <Button variant="danger" onClick={handleDeleteEntry}>
                        Vymazať
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default OperationalLogEntries;
